import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

class TheaterReservationService {
  constructor() {
    this.api = axios.create({
      baseURL: `${API_URL}/reservations`,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Interceptor para agregar token (admin o access_code)
    this.api.interceptors.request.use(
      (config) => {
        // Detectar el contexto basado en la URL actual
        const currentPath = window.location.pathname;
        const isAdminContext = currentPath.startsWith('/admin');

        const adminToken = localStorage.getItem('adminToken');
        const theaterToken = localStorage.getItem('theaterAccessCodeToken');

        let token = null;
        if (isAdminContext) {
          // Preferir admin token en contexto admin si existe
          token = adminToken || theaterToken || null;
        } else {
          // Contexto teatro
          token = theaterToken || null;
        }
        
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Interceptor para manejar errores
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Token expirado o inválido
          const hasAdmin = !!localStorage.getItem('adminToken');
          const hasTheater = !!localStorage.getItem('theaterAccessCodeToken');
          if (hasAdmin) {
            localStorage.removeItem('adminToken');
            localStorage.removeItem('adminData');
            window.location.href = '/admin';
          } else if (hasTheater) {
            localStorage.removeItem('theaterAccessCodeToken');
            localStorage.removeItem('theaterAuth');
            localStorage.removeItem('theaterUserCode');
            localStorage.removeItem('theaterUserData');
            localStorage.removeItem('theaterEventData');
            window.location.href = '/theater';
          } else {
            const currentPath = window.location.pathname;
            const isAdminContext = currentPath.startsWith('/admin');
            window.location.href = isAdminContext ? '/admin' : '/theater';
          }
        }
        return Promise.reject(error);
      }
    );
  }

  // Crear reservas
  async createReservation(reservationData) {
    try {
      const response = await this.api.post('/', reservationData);
      return response.data;
    } catch (error) {
      const errorMessage = this.getErrorMessage(error, 'Error al crear reserva');
      throw new Error(errorMessage);
    }
  }

  // Crear venta de entradas
  async createTicketSale(ticketSaleData) {
    try {
      const response = await this.api.post('/ticket-sale', ticketSaleData);
      return response.data;
    } catch (error) {
      const errorMessage = this.getErrorMessage(error, 'Error al crear venta de entradas');
      throw new Error(errorMessage);
    }
  }

  // Obtener reservas por Alumno
  async getReservationsByStudent(studentId, eventId = null) {
    try {
      const params = eventId ? { eventId } : {};
      const response = await this.api.get(`/student/${studentId}`, { params });
      return response.data;
    } catch (error) {
      const errorMessage = this.getErrorMessage(error, 'Error al obtener reservas del Alumno');
      throw new Error(errorMessage);
    }
  }

  // Obtener asientos ocupados de un evento
  async getOccupiedSeats(eventId) {
    try {
      const response = await this.api.get(`/event/${eventId}/occupied-seats`);
      return response.data;
    } catch (error) {
      const errorMessage = this.getErrorMessage(error, 'Error al obtener asientos ocupados');
      throw new Error(errorMessage);
    }
  }

  // Obtener asientos ocupados de un evento con estado de reserva
  async getOccupiedSeatsWithStatus(eventId) {
    try {
      const response = await this.api.get(`/event/${eventId}/occupied-seats-with-status`);
      return response.data;
    } catch (error) {
      const errorMessage = this.getErrorMessage(error, 'Error al obtener asientos ocupados con estado');
      throw new Error(errorMessage);
    }
  }

  // Consultar límite de tickets disponible
  async getTicketLimit(studentId, accessCodeId, newSeatsCount = 0, existingReservationsToCancel = '') {
    try {
      const params = { newSeatsCount };
      if (existingReservationsToCancel) {
        params.existingReservationsToCancel = existingReservationsToCancel;
      }
      
      const response = await this.api.get(`/ticket-limit/${studentId}/${accessCodeId}`, { params });
      return response.data;
    } catch (error) {
      const errorMessage = this.getErrorMessage(error, 'Error al consultar límite de tickets');
      throw new Error(errorMessage);
    }
  }

  // Verificar disponibilidad de un asiento
  async checkSeatAvailability(eventId, compositeId) {
    try {
      const response = await this.api.get('/check-availability', {
        params: { eventId, compositeId }
      });
      return response.data;
    } catch (error) {
      const errorMessage = this.getErrorMessage(error, 'Error al verificar disponibilidad');
      throw new Error(errorMessage);
    }
  }

  // Cancelar una reserva
  async cancelReservation(id) {
    try {
      const response = await this.api.patch(`/${id}/cancel`);
      return response.data;
    } catch (error) {
      const errorMessage = this.getErrorMessage(error, 'Error al cancelar reserva');
      throw new Error(errorMessage);
    }
  }

  // Obtener una reserva por ID
  async getReservationById(id) {
    try {
      const response = await this.api.get(`/${id}`);
      return response.data;
    } catch (error) {
      const errorMessage = this.getErrorMessage(error, 'Error al obtener reserva');
      throw new Error(errorMessage);
    }
  }

  // Validar código de acceso
  async validateAccessCode(code) {
    try {
      const response = await fetch(`${API_URL}/access-codes/validate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code, ticketSale: true }),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Código inválido');
      }
      
      const result = await response.json();
      return result;
    } catch (error) {
      const errorMessage = this.getErrorMessage(error, 'Error al validar el código de acceso');
      throw new Error(errorMessage);
    }
  }

  // Función auxiliar para manejar errores
  getErrorMessage(error, defaultMessage) {
    if (error.response?.data?.message) {
      return error.response.data.message;
    }
    if (error.message) {
      return error.message;
    }
    return defaultMessage;
  }
}

const theaterReservationService = new TheaterReservationService();
export default theaterReservationService; 